import { Alert, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { EmptyNote } from '../../components/RecordScreen';
import { enumLabel } from '../../lib/enums';
import { formatMoney, formatRateBp } from '../../lib/format';
import { marginTone } from '../inventory/stock';

export const MENU_PROFITABILITY_PATH = '/admin/v1/reports/menu-profitability';

interface MoneyRow {
  currency: string;
  chargedMinor: number;
  discountMinor: number;
  compMinor: number;
  billedMinor: number;
  netOfTaxMinor: number;
}

interface MarginRow {
  variantId: string;
  nameEn: string;
  qtySold: number;
  soldWithExtras: number;
  byCurrency: MoneyRow[];
  revenueBaseMinor: number;
  costBaseMinor: number;
  marginBaseMinor: number | null;
  marginBp: number | null;
  recipeFaults: number;
  unpricedIngredients: number;
  depletedNothing: boolean;
  voidedQty: number;
  voidedCostBaseMinor: number;
}

interface MenuProfitabilityReport {
  from: string;
  to: string;
  baseCurrency: string;
  rows: MarginRow[];
  billsCounted: number;
  billsWithNoRecordedRate: number;
  billsThatWouldNotAddUp: number;
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/**
 * Which dishes make money, and what should come off the menu.
 *
 * The two halves of this have existed separately for a while — the plate cost is rolled up and
 * checkable on the recipes screen, and every sale is recorded against the size that was rung — and
 * nothing put them together, so the one question the costing was built to answer could not be asked.
 *
 * ## What is on the page because leaving it off would make the page a lie
 *
 * **The margin is after tax, and the page says so.** The price on the board includes the ISV and the
 * tourism levy, and neither of them is the bar's money. A $14.00 burger keeps $11.76 of itself, and
 * comparing the ingredients against $14.00 overstates what the dish earns by nearly a fifth on every
 * plate — which is a large enough error to keep the wrong things on a menu.
 *
 * **The dishes that sold and moved no stock, above the table where they cannot be scrolled past.**
 * This is the most dangerous row this report can print and it is dangerous in the flattering
 * direction: a dish nobody has written a recipe for took real food off real shelves, none of it was
 * recorded, and every centavo of its price therefore reads as profit. The pour report learned the
 * same lesson about the same faults; the answer is the same one. Their margin column says it cannot
 * be worked out, because "we cannot work this out" and "this one is free" are opposite statements.
 *
 * **"At least" on a cost and "at most" on a margin**, in the recipes screen's own words, wherever an
 * ingredient has never been bought. A cost worked out from a floor gives a margin that is a ceiling
 * and can only come down, and it is shown without the healthy colour: a figure that is going to fall
 * is not evidence of a good one.
 *
 * **What was given away, apart from what was discounted.** A comped plate is not a plate sold badly
 * — it went out and nothing came in — and the two have opposite remedies: one is a word with the
 * staff, the other is a decision about a price. A dish here can therefore show a loss while its
 * price is perfectly sound, and the reader has to be able to see which of the two is happening.
 *
 * **Money in the money it was taken in.** A dish sold in lempiras is shown in lempiras beside the
 * converted figure, so that the conversion is visible rather than assumed. It is done at the rate
 * written onto each bill when it closed, never at one to one and never at today's rate.
 */
export function MenuProfitability({ from, to }: { from: string; to: string }) {
  const report = useQuery({
    queryKey: ['menu-profitability', from, to],
    queryFn: () =>
      rawApi<MenuProfitabilityReport>(
        'GET',
        `${MENU_PROFITABILITY_PATH}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: Boolean(from && to),
  });

  const base = report.data?.baseCurrency ?? 'USD';
  const rows = report.data?.rows ?? [];
  const noCost = rows.filter((r) => r.depletedNothing);
  const known = rows.filter((r) => r.marginBaseMinor !== null);
  const totals = known.reduce(
    (acc, r) => ({
      revenue: acc.revenue + r.revenueBaseMinor,
      cost: acc.cost + r.costBaseMinor,
      margin: acc.margin + (r.marginBaseMinor ?? 0),
    }),
    { revenue: 0, cost: 0, margin: 0 },
  );

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>What each dish makes</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm" maw={880}>
        What came in for every dish sold in these dates, against what the food actually cost to put out. The
        money is after tax — the price on the board includes the ISV and the tourism levy, and neither is
        yours — so "left over" is what the dish really contributed before wages and rent. An extra a guest
        paid for is counted on both sides: the money it added and the stock it used.
      </Text>

      {report.isError && <Alert color="red">That report could not be loaded.</Alert>}

      {!report.isLoading && report.data?.billsCounted === 0 && (
        <Alert color="gray" variant="light" title="No bills were closed in these dates" mb="sm">
          A dish only reaches this page once the bill it was on has been closed. An open tab has not sold
          anything yet.
        </Alert>
      )}

      {/* The reason a perfect margin is the most dangerous figure here, said above the table. A dish
          nobody has written a recipe for took food off three shelves and the ledger knows about none
          of it, so its whole price reads as profit. */}
      {noCost.length > 0 && (
        <Alert color="orange" title="Some dishes sold and nothing came off a shelf for them" mb="sm">
          <Text size="sm" mb="xs">
            Nothing was recorded leaving stock for these, so nothing here can say what they cost. They are in
            the table with no margin at all rather than a perfect one — a dish that appears to cost nothing
            would otherwise be the most profitable thing on your menu. Write a recipe for each and they start
            answering.
          </Text>
          <Stack gap={4}>
            {noCost.map((row) => (
              <Text size="sm" key={row.variantId}>
                <b>{row.nameEn || 'A dish that is no longer on the menu'}</b> — sold {row.qtySold}, took{' '}
                {formatMoney(row.revenueBaseMinor, base)} and cost nothing anybody can see
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {(report.data?.billsWithNoRecordedRate ?? 0) > 0 && (
        <Alert color="yellow" variant="light" mb="sm">
          <Text size="sm">
            {plural(report.data?.billsWithNoRecordedRate ?? 0, 'bill')} closed in these dates{' '}
            {report.data?.billsWithNoRecordedRate === 1 ? 'has' : 'have'} no exchange rate recorded against{' '}
            {report.data?.billsWithNoRecordedRate === 1 ? 'it' : 'them'}, so lempiras and dollars on{' '}
            {report.data?.billsWithNoRecordedRate === 1 ? 'it' : 'them'} could not honestly be stated as one
            figure. Nothing from {report.data?.billsWithNoRecordedRate === 1 ? 'it' : 'them'} is in the table
            below — no rate has been guessed at.
          </Text>
        </Alert>
      )}

      {(report.data?.billsThatWouldNotAddUp ?? 0) > 0 && (
        <Alert color="orange" variant="light" mb="sm">
          <Text size="sm">
            {plural(report.data?.billsThatWouldNotAddUp ?? 0, 'bill')} in these dates{' '}
            {report.data?.billsThatWouldNotAddUp === 1 ? 'has' : 'have'} more taken off than the bill is
            worth, which cannot be divided across the lines on it. Those bills are left out of the table
            below.
          </Text>
        </Alert>
      )}

      {!report.isLoading && rows.length === 0 && report.data?.billsCounted !== 0 && (
        <EmptyNote>Nothing was sold in these dates.</EmptyNote>
      )}

      {rows.length > 0 && (
        <Table.ScrollContainer minWidth={1040}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Dish</Table.Th>
                <Table.Th ta="right">Sold</Table.Th>
                <Table.Th ta="right">Came in</Table.Th>
                <Table.Th ta="right">Taken off</Table.Th>
                <Table.Th ta="right">Food cost</Table.Th>
                <Table.Th ta="right">Left over</Table.Th>
                <Table.Th ta="right">Of what came in</Table.Th>
                <Table.Th ta="right">Struck off</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => {
                // A cost that is short in either direction makes the margin beside it a ceiling. The
                // two absences have different remedies, so they are said separately and neither
                // borrows the other's words.
                const costIsShort = row.unpricedIngredients > 0 || row.recipeFaults > 0;
                // Money taken in a currency the books are not kept in, shown as well as converted so
                // that the conversion can be seen rather than assumed.
                const foreign = row.byCurrency.filter((c) => c.currency !== base && c.netOfTaxMinor !== 0);
                const takenOff = row.byCurrency.filter((c) => c.discountMinor !== 0 || c.compMinor !== 0);
                return (
                  <Table.Tr key={row.variantId}>
                    <Table.Td>
                      <Text fw={500}>{row.nameEn || 'A dish that is no longer on the menu'}</Text>
                      {row.depletedNothing && (
                        <Text size="xs" c="orange.7">
                          Nothing came off a shelf for this, so what it costs is unknown — not nothing.
                        </Text>
                      )}
                      {!row.depletedNothing && row.recipeFaults > 0 && (
                        <Text size="xs" c="orange.7">
                          Part of this recipe could not be worked out, so some of what it used is missing from
                          the cost.
                        </Text>
                      )}
                      {/* The recipes screen's own sentence about the same absence, so the two screens
                          say one thing. The remedy is a delivery, not a recipe. */}
                      {row.unpricedIngredients > 0 && (
                        <Text size="xs" c="yellow.7">
                          {row.unpricedIngredients === 1
                            ? 'An ingredient has'
                            : `${row.unpricedIngredients} ingredients have`}{' '}
                          never been bought in, so the cost counts them at nothing. Post a delivery and this
                          stops reading as more profit than it is.
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{row.qtySold}</Text>
                      {row.soldWithExtras > 0 && (
                        <Text size="xs" c="dimmed">
                          {row.soldWithExtras} with a paid extra
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatMoney(row.revenueBaseMinor, base)}</Text>
                      {foreign.map((c) => (
                        <Text size="xs" c="dimmed" key={c.currency}>
                          {formatMoney(c.netOfTaxMinor, c.currency)} of it taken in{' '}
                          {enumLabel('currency', c.currency).toLowerCase()}
                        </Text>
                      ))}
                    </Table.Td>
                    <Table.Td ta="right">
                      {takenOff.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      ) : (
                        // A comp is not a discount. Named apart because the two are fixed in
                        // completely different places, and a dish losing money purely because it is
                        // given away is not a dish that is priced wrong.
                        takenOff.map((c) => (
                          <Stack gap={0} key={c.currency}>
                            {c.compMinor !== 0 && (
                              <Text size="xs" c="orange.7">
                                {formatMoney(c.compMinor, c.currency)} given away
                              </Text>
                            )}
                            {c.discountMinor !== 0 && (
                              <Text size="xs" c="dimmed">
                                {formatMoney(c.discountMinor, c.currency)} discounted
                              </Text>
                            )}
                          </Stack>
                        ))
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatMoney(row.costBaseMinor, base)}</Text>
                      {costIsShort && (
                        <Text size="xs" c="orange.7">
                          at least
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      {row.marginBaseMinor === null ? (
                        <Text size="sm" c="dimmed">
                          Cannot be worked out
                        </Text>
                      ) : (
                        <Stack gap={0}>
                          <Text size="sm" fw={600}>
                            {formatMoney(row.marginBaseMinor, base)}
                          </Text>
                          {costIsShort && (
                            <Text size="xs" c="orange.7">
                              at most — it can only come down
                            </Text>
                          )}
                        </Stack>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      {/* No healthy colour on a figure that is going to fall, which is the same rule
                          the recipe list applies to the same arithmetic. */}
                      {row.marginBp === null ? (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      ) : (
                        <Text size="sm" fw={600} c={costIsShort ? undefined : marginTone(row.marginBp)}>
                          {formatRateBp(row.marginBp)}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      {/* Struck off a bill, so it is not a sale and its money is in none of the
                          columns to the left. Where the reason did not put the food back, what it
                          cost is money this dish lost and there is nowhere else it shows. */}
                      {row.voidedQty === 0 ? (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      ) : (
                        <Stack gap={0}>
                          <Text size="sm">{row.voidedQty}</Text>
                          {row.voidedCostBaseMinor !== 0 && (
                            <Text size="xs" c="orange.7">
                              {formatMoney(row.voidedCostBaseMinor, base)} of food not put back
                            </Text>
                          )}
                        </Stack>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {known.length > 0 && (
        <Text size="xs" c="dimmed" mt="sm" maw={880}>
          Across the {known.length} of {rows.length} dishes whose cost is known,{' '}
          {formatMoney(totals.revenue, base)} came in after tax and {formatMoney(totals.cost, base)} of food
          went out, leaving {formatMoney(totals.margin, base)}.
          {known.length < rows.length
            ? ` The other ${plural(rows.length - known.length, 'dish')} moved no stock at all and ${
                rows.length - known.length === 1 ? 'is' : 'are'
              } in none of those three figures.`
            : ''}{' '}
          This is food cost only — nothing here carries wages, gas, electricity or the ferry, so a dish that
          looks thin here is thinner still.
        </Text>
      )}
    </Paper>
  );
}
